// Search result cache using sessionStorage
// Caches search results keyed by a hash of the filters + orgId so the
// next reload of the same query doesn't re-hit Crustdata. Also persists
// the last search state per tab so results survive page refresh.
//
// **Tenant safety:** every read/write is scoped by orgId so a user
// switching organizations never sees the previous org's results, even
// inside the 5-minute TTL window. (Round-2 reliability audit S1.)

import type { CompanyCoverage } from '@/store/features/prospects.feature';
import { deferIdle } from '@/utils/defer';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
// The last search outlives the result cache: it is what Find People reopens
// on after a lead list. At 5 minutes, anyone who spent longer in the list came
// back to an empty page (and Back re-ran the search, re-billing it).
const STATE_TTL_MS = 60 * 60 * 1000;
// Bump this version when the API response shape changes so stale cache
// entries from a previous schema are automatically discarded.
//
// v3 (2026-04-19): full Crustdata v1 raw profile is now returned under
// ``result.raw`` so the new hidden columns (Degree, Past Company, etc.)
// can access nested fields. Pre-v3 cache entries may be missing
// education_background / past_employers / languages subtrees.
// v4 (2026-04-19): CompanyRecord gained follower_metrics,
// linkedin_company_description, linkedin_categories aliases. Pre-v4
// cache entries used the old shape with ``follower_count`` /
// ``description`` / ``specialties`` aliases; re-search to get the
// new shape.
// v5 (2026-05-24): cache keys + state keys now include orgId. Pre-v5
// entries silently leaked across org boundaries during the TTL window.
// v6 (2026-06-16): client-side paging. ``results`` now holds the
// ACCUMULATED stash across fetched blocks plus ``nextCursor`` /
// ``currentPage``. Pre-v6 entries stored only the first ~100-row block
// with no cursor, so treating them as a complete stash would wrongly
// disable "Next" when more rows exist — discard them.
// v7 (2026-09-01): People Search persists whether the successful response's
// ``total_count`` is only an upper bound after authoritative exclusions. That
// status must travel with the result set instead of being inferred from the
// currently edited filters.
const CACHE_VERSION = 'v7';
// Sentinel for callers that genuinely have no org context (anonymous,
// pre-auth, tests). Falls back to a stable string so cache still works
// in those edge cases without crashing — but in practice every search
// caller has a Clerk org id available.
const ORG_FALLBACK = 'noorg';

function normalizeOrg(orgId: string | null | undefined): string {
  return (orgId && orgId.trim()) || ORG_FALLBACK;
}

function peopleStateKey(orgId: string | null | undefined): string {
  return `cleanlist_people_search_state_${CACHE_VERSION}_${normalizeOrg(orgId)}`;
}

function companyStateKey(orgId: string | null | undefined): string {
  return `cleanlist_company_search_state_${CACHE_VERSION}_${normalizeOrg(orgId)}`;
}

function cachePrefix(orgId: string | null | undefined): string {
  return `cleanlist_search_cache_${CACHE_VERSION}_${normalizeOrg(orgId)}_`;
}

// All cache prefix matches across orgs — used by clearAllCachedResults
// when an enrichment lands and we want to invalidate every cached page
// the current user might re-open (any org they're a member of).
const ANY_CACHE_PREFIX = `cleanlist_search_cache_${CACHE_VERSION}_`;

// ─── Filter hashing ─────────────────────────────────────────────────
//
// Recursively sort keys so two logically-equivalent filter trees hash
// to the same value regardless of property insertion order. This is a
// replacement for the previous ``JSON.stringify(obj, Object.keys(obj))``
// approach, which had a nasty bug: JSON.stringify treats an array
// second arg as a key-whitelist applied at EVERY depth, so nested
// leaves like ``{column, type, value}`` collapsed to just ``{type}``
// because the top-level keys were ``['filters', 'op', 'type']``. That
// made every search hash to the same key and hit the cache — so
// changing a filter value (e.g. adding "51-200" to headcount) never
// triggered a refetch.
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
    }
    return sorted;
  }
  return value;
}

function hashFilters(filters: Record<string, unknown>): string {
  const str = JSON.stringify(sortKeysDeep(filters));
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return String(Math.abs(hash));
}

// ─── Result cache (dedup identical searches) ────────────────────────

interface CachedResult {
  results: unknown[];
  total: number;
  /** True when post-provider exclusions can make ``total`` an upper bound. */
  totalIsUpperBound?: boolean;
  timestamp: number;
  /** Client-side paging buffer state (see people/company search hooks).
   *  ``results`` holds the ACCUMULATED stash across every fetched block,
   *  not just one page — so a reload restores the paged position without
   *  re-billing Crustdata. ``nextCursor`` is the forward-only cursor for
   *  the next block (null = no more rows). Pre-paging cache entries omit
   *  both; callers default to ``nextCursor: null`` / ``currentPage: 1``. */
  nextCursor?: string | null;
  currentPage?: number;
  /** People-only: per-company coverage of the search, when it had one. */
  companyCoverage?: CompanyCoverage | null;
  /** People-only: the rows are closest name matches, not exact ones. */
  closestMatches?: boolean;
}

export function getCachedResults(
  filters: Record<string, unknown>,
  limit: number,
  orgId: string | null | undefined
): CachedResult | null {
  try {
    const key = cachePrefix(orgId) + hashFilters({ ...filters, limit });
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedResult;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

export function setCachedResults(
  filters: Record<string, unknown>,
  limit: number,
  results: unknown[],
  total: number,
  orgId: string | null | undefined
): void {
  try {
    const key = cachePrefix(orgId) + hashFilters({ ...filters, limit });
    sessionStorage.setItem(
      key,
      JSON.stringify({ results, total, timestamp: Date.now() })
    );
  } catch {
    // Quota exceeded - clear old entries
    clearExpiredCache();
  }
}

// ─── Async combined writer (off the main thread) ────────────────────
//
// ``setCachedResults`` + ``saveSearchState`` were called back-to-back after
// every search, each ``JSON.stringify``-ing the same ~3 MB result array
// synchronously on the main thread — a ~300-800 ms freeze right as results
// should paint. ``persistSearch`` replaces that pair: it serializes ONCE, off
// the main thread (a Web Worker), and is fire-and-forget (a slow or failed
// cache write never affects the UI). The caller paints first; this runs after.

export interface PersistSearchOptions {
  type: 'people' | 'company';
  /** Filters object used to build the result-cache key (sort-aware) — must
   *  match what ``getCachedResults`` is later called with. */
  cacheKeyFilters: Record<string, unknown>;
  /** Filters stored in the last-search-state entry (survives refresh). */
  stateFilters: Record<string, unknown>;
  limit: number;
  results: unknown[];
  total: number;
  /** People-only count semantics from the successful request. */
  totalIsUpperBound?: boolean;
  orgId: string | null | undefined;
  compositeCompanyFilters?: Record<string, unknown>;
  /** Forward-only cursor for the next block of results (null = no more).
   *  Persisted so a reload can keep paging from where the user left off
   *  instead of re-fetching block 1. */
  nextCursor?: string | null;
  /** Page the user is currently viewing within the accumulated stash. */
  currentPage?: number;
  /** People-only: per-company coverage of the search, when it had one. */
  companyCoverage?: CompanyCoverage | null;
  /** People-only: the rows are closest name matches, not exact ones. */
  closestMatches?: boolean;
}

let cacheWorker: Worker | null = null;
let cacheWorkerUnavailable = false;
let writeSeq = 0;
// id -> the two sessionStorage keys awaiting the worker's serialized output.
const pendingWrites = new Map<number, { cacheKey: string; stateKey: string }>();

function getCacheWorker(): Worker | null {
  if (cacheWorkerUnavailable) return null;
  if (cacheWorker) return cacheWorker;
  if (typeof Worker === 'undefined') {
    cacheWorkerUnavailable = true;
    return null;
  }
  try {
    cacheWorker = new Worker(
      new URL('./search-cache.worker.ts', import.meta.url),
      { type: 'module' }
    );
    cacheWorker.onmessage = (
      e: MessageEvent<{ id: number; cacheJson: string; stateJson: string }>
    ) => {
      const { id, cacheJson, stateJson } = e.data;
      const target = pendingWrites.get(id);
      if (!target) return;
      pendingWrites.delete(id);
      try {
        sessionStorage.setItem(target.cacheKey, cacheJson);
        sessionStorage.setItem(target.stateKey, stateJson);
      } catch {
        clearExpiredCache();
      }
    };
    cacheWorker.onerror = () => {
      // Worker blew up — stop using it and let future writes go inline.
      pendingWrites.clear();
      cacheWorkerUnavailable = true;
      cacheWorker = null;
    };
  } catch {
    cacheWorkerUnavailable = true;
    cacheWorker = null;
  }
  return cacheWorker;
}

/** Serialize on a fallback path (no Worker): still deferred to idle so it
 *  doesn't block paint, just not off-thread. */
function writeInline(
  cacheKey: string,
  stateKey: string,
  cacheValue: unknown,
  stateValue: unknown,
  id: number
): void {
  deferIdle(() => {
    if (!pendingWrites.has(id)) return;
    pendingWrites.delete(id);
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheValue));
      sessionStorage.setItem(stateKey, JSON.stringify(stateValue));
    } catch {
      clearExpiredCache();
    }
  });
}

/** Persist both the result cache and the last-search state for a completed
 *  search, off the main thread. Fire-and-forget. */
export function persistSearch(opts: PersistSearchOptions): void {
  const timestamp = Date.now();
  const cacheKey =
    cachePrefix(opts.orgId) +
    hashFilters({ ...opts.cacheKeyFilters, limit: opts.limit });
  const stateKey =
    opts.type === 'people'
      ? peopleStateKey(opts.orgId)
      : companyStateKey(opts.orgId);

  const cacheValue = {
    results: opts.results,
    total: opts.total,
    totalIsUpperBound: opts.totalIsUpperBound ?? false,
    nextCursor: opts.nextCursor ?? null,
    currentPage: opts.currentPage ?? 1,
    companyCoverage: opts.companyCoverage ?? null,
    closestMatches: opts.closestMatches ?? false,
    timestamp,
  };
  const stateValue = {
    filters: opts.stateFilters,
    compositeCompanyFilters: opts.compositeCompanyFilters,
    results: opts.results,
    total: opts.total,
    totalIsUpperBound: opts.totalIsUpperBound ?? false,
    nextCursor: opts.nextCursor ?? null,
    currentPage: opts.currentPage ?? 1,
    companyCoverage: opts.companyCoverage ?? null,
    closestMatches: opts.closestMatches ?? false,
    timestamp,
  };

  // A newer People Search or Clear All owns this state key. Late worker/idle
  // replies must not restore invalidated rows. Company caching is unchanged.
  if (opts.type === 'people') {
    for (const [pendingId, target] of pendingWrites) {
      if (target.stateKey === stateKey) pendingWrites.delete(pendingId);
    }
  }
  const id = ++writeSeq;
  pendingWrites.set(id, { cacheKey, stateKey });
  const worker = getCacheWorker();
  if (!worker) {
    writeInline(cacheKey, stateKey, cacheValue, stateValue, id);
    return;
  }
  // The postMessage structured-clone of the result array is itself main-thread
  // work, so even that is deferred to idle. The worker then does the heavy
  // stringify and posts the JSON back for a cheap setItem.
  deferIdle(() => {
    if (!pendingWrites.has(id)) return;
    try {
      worker.postMessage({ id, cacheValue, stateValue });
    } catch {
      writeInline(cacheKey, stateKey, cacheValue, stateValue, id);
    }
  });
}

function clearExpiredCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(ANY_CACHE_PREFIX)) {
        try {
          const raw = sessionStorage.getItem(key);
          if (raw) {
            const cached = JSON.parse(raw) as CachedResult;
            if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key!);
        }
      }
    }
    for (const key of keysToRemove) sessionStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

/** Drop cached search result pages. When called with
 *  ``{ personId }`` only entries whose results array contains that
 *  person are purged — the typical "one enrichment landed, refresh the
 *  pages that show this person" case. Without a filter, every page
 *  across every org is purged (used by org-switch and explicit clear-all
 *  flows).
 *
 *  Why the targeted variant matters: with ~50 cached search pages in
 *  sessionStorage, the nuke version forces 50 follow-up Crustdata
 *  fetches the next time the user opens any saved search — even ones
 *  unrelated to the enriched person. Scoping by ``personId`` keeps the
 *  cache warm for everything else. */
export function clearAllCachedResults(options?: { personId?: string }): void {
  try {
    const personId = options?.personId;
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(ANY_CACHE_PREFIX)) continue;
      if (!personId) {
        // No filter — match legacy behavior, drop everything.
        keysToRemove.push(key);
        continue;
      }
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) continue;
        const cached = JSON.parse(raw) as CachedResult;
        if (!Array.isArray(cached.results)) continue;
        const hit = cached.results.some(
          (r) =>
            r &&
            typeof r === 'object' &&
            'id' in (r as Record<string, unknown>) &&
            (r as { id?: unknown }).id === personId
        );
        if (hit) keysToRemove.push(key);
      } catch {
        // Corrupt entry — safer to drop than to leave a poison-pill
        // that throws on every subsequent load.
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) sessionStorage.removeItem(key);
  } catch {
    // Ignore — caller is best-effort.
  }
}

// ─── Last search state persistence (survive refresh) ────────────────

interface SearchState {
  filters: Record<string, unknown>;
  /** Optional parallel state slice for People searches — the People
   *  hook holds a separate ``compositeCompanyFilters`` that must round-
   *  trip with ``filters`` so a page reload preserves a firmographic
   *  query that was routed through /people-search/by-companies. */
  compositeCompanyFilters?: Record<string, unknown>;
  results: unknown[];
  total: number;
  /** True when post-provider exclusions can make ``total`` an upper bound. */
  totalIsUpperBound?: boolean;
  /** Accumulated-stash paging state — see CachedResult. Optional so
   *  pre-paging state blobs still load (default null / page 1). */
  nextCursor?: string | null;
  currentPage?: number;
  /** People-only: per-company coverage of the search, when it had one. */
  companyCoverage?: CompanyCoverage | null;
  /** People-only: the rows are closest name matches, not exact ones. */
  closestMatches?: boolean;
  timestamp: number;
}

export function saveSearchState(
  type: 'people' | 'company',
  filters: Record<string, unknown>,
  results: unknown[],
  total: number,
  orgId: string | null | undefined,
  compositeCompanyFilters?: Record<string, unknown>
): void {
  try {
    const key =
      type === 'people' ? peopleStateKey(orgId) : companyStateKey(orgId);
    sessionStorage.setItem(
      key,
      JSON.stringify({
        filters,
        compositeCompanyFilters,
        results,
        total,
        timestamp: Date.now(),
      })
    );
  } catch {
    // Ignore
  }
}

// The page a search was left on, kept apart from the results blob so paging
// never re-serializes them. Coming back from a lead list (or any page) always
// landed on page 1.
function pageKey(
  type: 'people' | 'company',
  orgId: string | null | undefined
): string {
  return `${type === 'people' ? peopleStateKey(orgId) : companyStateKey(orgId)}:page`;
}

export function saveSearchPage(
  type: 'people' | 'company',
  orgId: string | null | undefined,
  page: number
): void {
  try {
    sessionStorage.setItem(pageKey(type, orgId), String(page));
  } catch {
    // Ignore
  }
}

/** Write fields into one row of the saved search (a reveal's value), so the
 *  search survives leaving the page. Clearing the whole state for that lost
 *  the search: back from a list, Find People opened empty. */
export function patchSearchStateRow(
  type: 'people' | 'company',
  orgId: string | null | undefined,
  rowId: string,
  patch: Record<string, unknown>
): void {
  try {
    const key =
      type === 'people' ? peopleStateKey(orgId) : companyStateKey(orgId);
    const raw = sessionStorage.getItem(key);
    if (!raw) return;
    const state = JSON.parse(raw) as SearchState;
    state.results = state.results.map((row) =>
      (row as { id?: string }).id === rowId
        ? { ...(row as object), ...patch }
        : row
    );
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Ignore
  }
}

/** The saved page when the restored rows still reach it, else the state's. */
export function restoredSearchPage(
  type: 'people' | 'company',
  orgId: string | null | undefined,
  state: Pick<SearchState, 'results' | 'currentPage'> | null,
  pageSize: number
): number {
  try {
    const saved = Number(sessionStorage.getItem(pageKey(type, orgId)));
    const held = Math.ceil((state?.results?.length ?? 0) / pageSize);
    if (Number.isInteger(saved) && saved >= 1 && saved <= held) return saved;
  } catch {
    // Ignore
  }
  return state?.currentPage ?? 1;
}

export function clearSearchState(
  type: 'people' | 'company',
  orgId: string | null | undefined
): void {
  try {
    sessionStorage.removeItem(pageKey(type, orgId));
    const key =
      type === 'people' ? peopleStateKey(orgId) : companyStateKey(orgId);
    // Cancel in-flight writes for this key so a late worker/idle reply
    // cannot restore the state we are clearing.
    for (const [id, target] of pendingWrites) {
      if (target.stateKey === key) pendingWrites.delete(id);
    }
    sessionStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

export function loadSearchState(
  type: 'people' | 'company',
  orgId: string | null | undefined
): SearchState | null {
  try {
    const key =
      type === 'people' ? peopleStateKey(orgId) : companyStateKey(orgId);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const state = JSON.parse(raw) as SearchState;
    if (Date.now() - state.timestamp > STATE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return state;
  } catch {
    return null;
  }
}
